/**
 * The target DC size auto-design searches for, kept apart from the capacity the
 * plant actually has.
 *
 * `pvInstalledCap.kw` used to be both at once. Before a design existed it was
 * the target the search aimed at; after one was saved it became a statement
 * about the design. One field answering two questions cannot be right in both
 * roles: writing the design's capacity back into it silently destroyed the
 * target, and a target typed for the next search silently mis-stated the plant
 * that was already built - which is how an 1,800-module plant came to read
 * 1,062 kWp while its modules made 900.
 *
 * The target now lives in the auto-design dialog beside the other search
 * inputs, and the outer field is left to describe the plant. These helpers are
 * the seam: the target is read through `resolveTargetKw`, never straight off
 * the config, so the fallback below stays in one place.
 */

function positive(value) {
  const out = Number(value);
  return Number.isFinite(out) && out > 0 ? out : null;
}

/**
 * The target a saved project carries, or null when it has never set one.
 *
 * Deliberately NOT falling back here: the caller needs to tell "no target of
 * its own" apart from "a target that happens to equal the capacity", because
 * the two look different on screen.
 */
export function storedTargetKw(config) {
  return positive(config?.autoDesign?.targetCapacityKw);
}

/**
 * The target to search with, in kW, or null when there is none to use.
 *
 * Projects saved before the target existed have only `pvInstalledCap.kw`, and
 * for them that value IS the target they were last designed against - so it is
 * what the search must keep using, or re-running auto-design on an old project
 * would silently change its size.
 */
export function resolveTargetKw(config) {
  return storedTargetKw(config) ?? positive(config?.pvInstalledCap?.kw);
}

/**
 * Where `resolveTargetKw` got its answer: "target", "capacity" (the fallback,
 * worth saying out loud on screen) or "none".
 */
export function targetSource(config) {
  if (storedTargetKw(config) !== null) return "target";
  if (positive(config?.pvInstalledCap?.kw) !== null) return "capacity";
  return "none";
}

/**
 * Is the installed-capacity field a statement about a design rather than a
 * number someone types?
 *
 * Once a design is saved the capacity is module count times module rating and
 * nothing else, so the field is shown locked. With no design there is nothing
 * to derive it from and it stays editable - which is also how a project that
 * predates the target field still has somewhere to put one.
 */
export function capacityIsDerived(moduleCount) {
  return positive(moduleCount) !== null;
}
